/**
 * GenericCrudPage — 通用 CRUD 管理页组件
 *
 * 一个 ProTable + ModalForm 的组合, 由 CrudConfig 驱动:
 *   - 表格列从 fields 自动推导 (排除 hideInTable)
 *   - 搜索栏从 searchFields 自动启用
 *   - 新建/编辑 modal 表单从 fields 自动渲染 (排除 hideInForm/readonly)
 *   - 删除走 DRF DELETE (后端 BaseViewSet 默认软删)
 *
 * 使用方式:
 *   import { GenericCrudPage } from '@/components/GenericCrudPage';
 *   import { configs } from '@/pages/configs';
 *   <GenericCrudPage config={configs.faq} />
 *
 * 字段类型 (FieldConfig.type):
 *   text / textarea / number / select / datetime / boolean / json / readonly
 *
 * 备注:
 *   - 真正的多语言字段编辑(name_translations 等)由各 ViewSet 自己的定制 Form 处理
 *     (见 src/pages/industries/Form.tsx 的多 Tab 实现); 这里只暴露 display_name 等
 *     已渲染好的只读字段, 避免泛化过度.
 *   - 详情视图当前复用 ModalForm; 后续可单独扩 Drawer 详情页.
 */
import { useRef, useState, type ReactNode } from 'react';
import {
  PageContainer,
  ProTable,
  ModalForm,
  ProFormText,
  ProFormSelect,
  ProFormDateTimePicker,
  ProFormDigit,
  ProFormTextArea,
  ProFormSwitch,
  type ActionType,
  type ProColumns,
} from '@ant-design/pro-components';
import { Button, Popconfirm, Tag, message } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { apiClient } from '@/api/client';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'datetime'
  | 'boolean'
  | 'json'
  | 'readonly';

export interface FieldOption {
  label: string;
  value: string | number | boolean;
  /** ProTable valueEnum 用的状态标记 (Success/Default/Processing/Error/Warning) */
  status?: 'Success' | 'Default' | 'Processing' | 'Error' | 'Warning';
  /** Tag 颜色 (留空走默认) */
  color?: string;
}

export interface FieldConfig {
  /** 字段名 (对应后端 model 字段) */
  name: string;
  /** 中文显示名 */
  label: string;
  /** 字段类型, 控制表格列与表单控件 */
  type: FieldType;
  /** 表单必填 */
  required?: boolean;
  /** select 选项 */
  options?: FieldOption[];
  /** 表格列宽 */
  width?: number;
  /** 隐藏在表格中 */
  hideInTable?: boolean;
  /** 隐藏在表单中 */
  hideInForm?: boolean;
  /** 隐藏在搜索栏 (默认所有 search 字段都展示) */
  hideInSearch?: boolean;
  /** 表单只读 (用于 id/uuid/created_at 这种系统字段) */
  readonly?: boolean;
  /** 表格列复制按钮 */
  copyable?: boolean;
  /** 文本省略号 */
  ellipsis?: boolean;
  /** 自定义提示 */
  tooltip?: string;
  /** 占位符 */
  placeholder?: string;
}

export interface CrudConfig {
  /** API 资源名, 拼到 /admin/v1/{resource}/ */
  resource: string;
  /** 页面标题 */
  title: string;
  /** 字段定义 */
  fields: FieldConfig[];
  /** 哪些字段加进 ProTable 搜索栏 (字段 name 数组); 留空则关闭搜索 */
  searchFields?: string[];
  /** 默认分页大小 */
  pageSize?: number;
  /** 主键字段, 默认 'id' (有些资源用 uuid) */
  rowKey?: string;
  /** 是否禁用新建按钮 (例如 LoginHistory 这种只读资源) */
  disableCreate?: boolean;
  /** 是否禁用编辑 */
  disableEdit?: boolean;
  /** 是否禁用删除 */
  disableDelete?: boolean;
  /** 额外的表格操作 (例如 "重置密码") */
  extraRowActions?: (record: any, reload: () => void) => ReactNode[];
}

function buildOptionsMap(options?: FieldOption[]) {
  if (!options) return undefined;
  const map: Record<string, { text: string; status?: string }> = {};
  options.forEach((o) => {
    map[String(o.value)] = { text: o.label, status: o.status };
  });
  return map;
}

function renderCellValue(field: FieldConfig, value: any) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: '#bbb' }}>-</span>;
  }
  if (field.type === 'select' && field.options) {
    const opt = field.options.find((o) => String(o.value) === String(value));
    if (opt) {
      return <Tag color={opt.color}>{opt.label}</Tag>;
    }
  }
  if (field.type === 'boolean') {
    return <Tag color={value ? 'green' : 'default'}>{value ? '是' : '否'}</Tag>;
  }
  if (field.type === 'json') {
    return (
      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
        {typeof value === 'object' ? JSON.stringify(value).slice(0, 60) : String(value).slice(0, 60)}
      </span>
    );
  }
  return String(value);
}

function renderFormField(f: FieldConfig, disabled = false) {
  const common = {
    name: f.name,
    label: f.label,
    tooltip: f.tooltip,
    placeholder: f.placeholder,
    disabled: disabled || f.readonly,
    rules: f.required ? [{ required: true, message: `请填写 ${f.label}` }] : undefined,
    key: f.name,
  };
  switch (f.type) {
    case 'textarea':
      return <ProFormTextArea {...common} />;
    case 'number':
      return <ProFormDigit {...common} />;
    case 'select':
      return (
        <ProFormSelect
          {...common}
          options={f.options?.map((o) => ({ label: o.label, value: o.value as string | number }))}
        />
      );
    case 'datetime':
      return <ProFormDateTimePicker {...common} />;
    case 'boolean':
      return <ProFormSwitch {...common} />;
    case 'json':
      return <ProFormTextArea {...common} placeholder={f.placeholder || '请输入合法 JSON'} />;
    case 'readonly':
    case 'text':
    default:
      return <ProFormText {...common} />;
  }
}

function buildColumns(
  config: CrudConfig,
  onEdit: (record: any) => void,
  onDelete: (id: any) => Promise<void>,
  reload: () => void,
): ProColumns<any>[] {
  const rowKey = config.rowKey || 'id';
  const searchSet = new Set(config.searchFields || []);

  const columns: ProColumns<any>[] = config.fields
    .filter((f) => !f.hideInTable)
    .map((f) => {
      const valueEnum = f.type === 'select' ? buildOptionsMap(f.options) : undefined;
      const search = searchSet.has(f.name) && !f.hideInSearch ? undefined : false;
      return {
        title: f.label,
        dataIndex: f.name,
        width: f.width,
        copyable: f.copyable,
        ellipsis: f.ellipsis,
        valueEnum: valueEnum as any,
        search,
        tooltip: f.tooltip,
        render: (_: any, record: any) => renderCellValue(f, record[f.name]),
      } as ProColumns<any>;
    });

  const showActions =
    !config.disableEdit || !config.disableDelete || !!config.extraRowActions;

  if (showActions) {
    columns.push({
      title: '操作',
      valueType: 'option',
      width: 200,
      fixed: 'right',
      render: (_: any, record: any) => {
        const nodes: ReactNode[] = [];
        if (!config.disableEdit) {
          nodes.push(
            <a key="edit" onClick={() => onEdit(record)}>
              <EditOutlined /> 编辑
            </a>,
          );
        }
        if (!config.disableDelete) {
          nodes.push(
            <Popconfirm
              key="del"
              title="确认删除该记录?"
              description="后端默认软删除, 可由 superuser 恢复"
              onConfirm={() => onDelete(record[rowKey])}
            >
              <a style={{ color: '#ff4d4f' }}>删除</a>
            </Popconfirm>,
          );
        }
        if (config.extraRowActions) {
          nodes.push(...config.extraRowActions(record, reload));
        }
        return nodes;
      },
    });
  }

  return columns;
}

export function GenericCrudPage({ config }: { config: CrudConfig }) {
  const actionRef = useRef<ActionType>();
  const [editing, setEditing] = useState<any | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const rowKey = config.rowKey || 'id';

  async function handleDelete(id: any) {
    try {
      await apiClient.delete(`/admin/v1/${config.resource}/${id}/`);
      message.success('删除成功');
      actionRef.current?.reload();
    } catch {
      // client 拦截器已 toast
    }
  }

  function openEdit(record: any) {
    setEditing(record);
    setEditVisible(true);
  }

  function reload() {
    actionRef.current?.reload();
  }

  const columns = buildColumns(config, openEdit, handleDelete, reload);

  return (
    <PageContainer header={{ title: config.title }}>
      <ProTable<any>
        actionRef={actionRef}
        columns={columns}
        rowKey={rowKey}
        scroll={{ x: 'max-content' }}
        request={async (params) => {
          const { current, pageSize, ...rest } = params;
          try {
            const resp: any = await apiClient.get(`/admin/v1/${config.resource}/`, {
              params: { page: current, page_size: pageSize, ...rest },
            });
            // client 拦截器已 unwrap: {pagination, results/data} or raw array
            const data = resp?.results || resp?.data || (Array.isArray(resp) ? resp : []);
            const total = resp?.pagination?.count ?? data.length ?? 0;
            return { data, success: true, total };
          } catch {
            return { data: [], success: false, total: 0 };
          }
        }}
        toolBarRender={() =>
          config.disableCreate
            ? []
            : [
                <CreateModalForm
                  key="create"
                  config={config}
                  onSuccess={reload}
                />,
              ]
        }
        search={config.searchFields?.length ? { labelWidth: 'auto' } : false}
        pagination={{ pageSize: config.pageSize || 20, showSizeChanger: true }}
      />

      {editing && (
        <EditModalForm
          config={config}
          record={editing}
          visible={editVisible}
          onClose={() => {
            setEditVisible(false);
            setEditing(null);
          }}
          onSuccess={() => {
            setEditVisible(false);
            setEditing(null);
            reload();
          }}
        />
      )}
    </PageContainer>
  );
}

function CreateModalForm({
  config,
  onSuccess,
}: {
  config: CrudConfig;
  onSuccess: () => void;
}) {
  return (
    <ModalForm
      title={`新建 ${config.title}`}
      width={640}
      trigger={
        <Button type="primary" icon={<PlusOutlined />}>
          新建
        </Button>
      }
      modalProps={{ destroyOnClose: true }}
      onFinish={async (values) => {
        try {
          await apiClient.post(`/admin/v1/${config.resource}/`, values);
          message.success('创建成功');
          onSuccess();
          return true;
        } catch {
          return false;
        }
      }}
    >
      {config.fields
        .filter((f) => !f.hideInForm && !f.readonly)
        .map((f) => renderFormField(f))}
    </ModalForm>
  );
}

function EditModalForm({
  config,
  record,
  visible,
  onClose,
  onSuccess,
}: {
  config: CrudConfig;
  record: any;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const rowKey = config.rowKey || 'id';
  return (
    <ModalForm
      title={`编辑 ${config.title}`}
      width={640}
      open={visible}
      initialValues={record}
      modalProps={{ destroyOnClose: true, onCancel: onClose }}
      onFinish={async (values) => {
        try {
          await apiClient.patch(`/admin/v1/${config.resource}/${record[rowKey]}/`, values);
          message.success('已更新');
          onSuccess();
          return true;
        } catch {
          return false;
        }
      }}
    >
      {config.fields
        .filter((f) => !f.hideInForm)
        .map((f) => renderFormField(f, f.readonly))}
    </ModalForm>
  );
}

export default GenericCrudPage;
